import { useMemo } from 'react';
import { APITypes, MedicalDataTypes } from '../../primary-record-types';
import useMedicalProfileData, { GroupedResource } from './useMedicalProfileData';

export type UseMedicalProfileResourceArgs = {
    medicalProfileId: string;
    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
    resourceId: string;
};

export type UseMedicalProfileResourceReturnValue = {
    resourceGroup: GroupedResource | null;
    resource: APITypes.medicalDataService.ListResourceResponseItem | null;
    isLoading: boolean;
};

export default function useMedicalProfileResource({
    medicalProfileId,
    resourceCategory,
    resourceId,
}: UseMedicalProfileResourceArgs): UseMedicalProfileResourceReturnValue {
    const { groupedResources, isLoadingResources, resources } = useMedicalProfileData({
        medicalProfileId,
        resourceCategory,
    });

    const resourceGroup = useMemo(() => {
        if (!groupedResources?.length) return null;

        return (
            groupedResources.find(
                (resource) =>
                    resource.id === resourceId ||
                    !!resource.groupSubResources?.find((subResource) => subResource.id === resourceId),
            ) || null
        );
    }, [resourceId, groupedResources]);

    const resource = useMemo(() => {
        if (!resources?.resources?.length) return null;

        return resources?.resources?.find((_) => _.id === resourceId) || null;
    }, [resourceId, resources]);

    return {
        resource,
        resourceGroup,
        isLoading: isLoadingResources,
    };
}
